import { defineStore } from 'pinia'
import type { components } from '~/api/api'
import client from '~/api/client'
import { getApiBase } from '~/utils/api-base'
import { resetEditTokens } from '~/composables/useEditToken'

let isRefreshing = false
const pendingClones = new WeakMap<Request, Request>()

export const useAuthStore = defineStore('auth', () => {
  const router = useRouter()
  const user = ref<components['schemas']['UserOut'] | undefined>(undefined)
  const webs = ref<components['schemas']['WebSummary'][]>([])
  // false = current terms version not accepted yet → TermsAcceptModal (layouts).
  const termsAccepted = ref(true)
  const accessToken = ref<string | null>(
    window.localStorage.getItem('accessToken') || window.sessionStorage.getItem('accessToken'),
  )
  const refreshToken = ref<string | null>(
    window.localStorage.getItem('refreshToken') || window.sessionStorage.getItem('refreshToken'),
  )

  // Navigate to login when user transitions from logged-in to logged-out (mid-session 401).
  // This watch does NOT fire on initial load (user was never set → oldValue is undefined).
  watch(user, (newValue, oldValue) => {
    if (oldValue && !newValue) {
      router.push('/auth/login')
    }
  })

  client.use({
    async onRequest({ request }) {
      pendingClones.set(request, request.clone())
      if (accessToken.value) {
        request.headers.set('authorization', `Bearer ${accessToken.value}`)
      }
      return request
    },
    async onResponse({ request, response }) {
      if (response.status === 401 && !isRefreshing) {
        if (refreshToken.value) {
          isRefreshing = true
          const refreshed = await doRefresh(refreshToken.value)
          isRefreshing = false
          if (refreshed) {
            const clone = pendingClones.get(request)
            pendingClones.delete(request)
            if (clone) {
              const headers = new Headers(clone.headers)
              headers.set('authorization', `Bearer ${accessToken.value}`)
              return fetch(new Request(clone, { headers }))
            }
          }
        }
        doLogout()
      }
      pendingClones.delete(request)
      return response
    },
  })

  async function doRefresh(token: string): Promise<boolean> {
    try {
      const res = await fetch(`${getApiBase()}/api/auth/refresh-token/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: token }),
      })
      if (!res.ok) return false
      const data = await res.json()
      if (!data.access_token) return false
      const remember = !!window.localStorage.getItem('accessToken')
      persistTokens(data.access_token, data.refresh_token, remember)
      return true
    } catch {
      return false
    }
  }

  function persistTokens(access: string | null | undefined, refresh: string | null | undefined, remember = true) {
    accessToken.value = access ?? null
    refreshToken.value = refresh ?? null

    if (!accessToken.value || !refreshToken.value) {
      return false
    }

    const storage = remember ? window.localStorage : window.sessionStorage
    storage.setItem('accessToken', accessToken.value)
    storage.setItem('refreshToken', refreshToken.value)
    return true
  }

  async function login(email: string, password: string, remember = true) {
    const { data: res, error } = await client.POST('/api/auth/login', { body: { email, password } })
    if (error) {
      throw (error as any)?.detail || 'Neplatné přihlašovací údaje'
    }
    if (!persistTokens(res?.access_token, res?.refresh_token, remember)) {
      throw 'Neplatné přihlašovací údaje'
    }
    resetEditTokens()
  }

  async function loadMe() {
    const { data, error } = await client.GET('/api/auth/me')
    if (error || !data) {
      throw 'Not authorized'
    }
    user.value = data.user
    webs.value = data.webs ?? []
    termsAccepted.value = (data as { terms_accepted?: boolean }).terms_accepted !== false
  }

  async function changePassword(currentPassword: string, newPassword: string) {
    const { data, error } = await client.POST('/api/auth/change-password', {
      body: { current_password: currentPassword, new_password: newPassword },
    })
    if (error) {
      throw (error as any)?.detail || 'Změna hesla selhala'
    }
    // The server revoked every token minted before the change — including ours.
    // Keep this session alive on the fresh pair it returned.
    const fresh = data as unknown as { access_token?: string; refresh_token?: string }
    if (fresh.access_token && fresh.refresh_token) {
      const remember = !!window.localStorage.getItem('accessToken')
      persistTokens(fresh.access_token, fresh.refresh_token, remember)
    }
    // Inline-edit tokens carry token_version — the old ones are dead now.
    resetEditTokens()
    user.value = data
  }

  function doLogout() {
    resetEditTokens()
    accessToken.value = null
    refreshToken.value = null
    user.value = undefined
    webs.value = []
    window.localStorage.removeItem('accessToken')
    window.localStorage.removeItem('refreshToken')
    window.sessionStorage.removeItem('accessToken')
    window.sessionStorage.removeItem('refreshToken')
  }

  function logout() {
    doLogout()
  }

  // accessToken exposed for the few raw-fetch callers outside the openapi
  // client (demo claim); the interceptor above stays the normal path.
  return { user, webs, termsAccepted, accessToken, login, persistTokens, loadMe, changePassword, logout }
})
